import { FC } from 'react';
import { useSelector } from 'react-redux';

import { Tag } from 'antd';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { RawDictionaryData } from 'types/dictionaries';

import { Dictionaries, LanguageLevel } from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import getSingleLabelFromDictionary from 'utils/getInfo/getSingleLabelFromDictionary';

import styles from './LanguageWithLevel.module.scss';

interface LanguageWithLevelProps {
  langs: RawDictionaryData | null;
  isDisplayAsTags?: boolean;
}

const levelClassMapping: Record<string, string> = {
  [LanguageLevel.Advanced]: styles.advanced,
  [LanguageLevel.Beginner]: styles.beginner,
  [LanguageLevel.Intermediate]: styles.intermediate,
  [LanguageLevel.Native]: styles.native,
  [LanguageLevel.PreIntermediate]: styles.preIntermediate
};

const LanguageWithLevel: FC<LanguageWithLevelProps> = ({
  langs,
  isDisplayAsTags
}) => {
  const languagesDict = useSelector(selectDictionary(Dictionaries.Language));
  const languageLevelOptions = useSelector(
    selectDictionary(Dictionaries.LanguageLevel)
  );

  const formattedLanguages = langs
    ? Object.keys(langs).map((language) => {
        const levelValue = langs[language] as string;
        const levelLabel = getSingleLabelFromDictionary(
          languageLevelOptions,
          levelValue
        );
        const levelClassName = levelClassMapping[levelValue] ?? '';

        const translatedLanguageLabel = getSingleLabelFromDictionary(
          languagesDict,
          language.toUpperCase()
        );
        const capitalizedLanguageKey =
          language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
        const lang = translatedLanguageLabel || capitalizedLanguageKey;

        if (isDisplayAsTags) {
          const title = `${lang} (${levelLabel})`;
          return (
            <Tag bordered={false} key={title}>
              {title}
            </Tag>
          );
        }

        return (
          <div className={styles.languageItem} key={language}>
            <div className={styles.languageName}>{lang}</div>
            {levelLabel && (
              <div className={`${styles.languageLevel} ${levelClassName}`}>
                ({levelLabel})
              </div>
            )}
          </div>
        );
      })
    : FALLBACK_VALUE;

  return <div className={styles.container}>{formattedLanguages}</div>;
};

export default LanguageWithLevel;
